import { ButtonGroup } from "@themes/pollinator/ui/ButtonGroup";

export type TabValue = "schedule" | "cohort" | "surveys";

export type Props = {
  /** Currently active tab */
  activeTab: TabValue;
  /** Callback when tab is clicked */
  onTabChange: (tab: TabValue) => void;
  /** Whether to show the Schedule tab */
  showSchedule: boolean;
  /** Whether to show the Cohort tab */
  showCohort: boolean;
  /** Whether to show the Surveys tab */
  showSurveys?: boolean;
  /** Additional CSS classes */
  className?: string;
};

/**
 * Tab navigation for the Program page.
 * Uses ButtonGroup with tabs layout for consistent styling.
 * Designed to be used inside AppPageHeader's tabs prop.
 * Only renders tabs that should be visible based on user permissions.
 */
export function ProgramTabs({
  activeTab,
  onTabChange,
  showSchedule,
  showCohort,
  showSurveys = false,
  className,
}: Props) {
  const tabs = [
    ...(showSchedule
      ? [{ label: "Schedule", value: "schedule" as const }]
      : []),
    ...(showCohort ? [{ label: "Cohort", value: "cohort" as const }] : []),
    ...(showSurveys
      ? [{ label: "Surveys", value: "surveys" as const }]
      : []),
  ];

  // Don't render if no tabs are visible
  if (tabs.length === 0) {
    return null;
  }

  return (
    <div className={className} data-testid="program-tabs">
      <ButtonGroup
        layout="tabs"
        buttons={tabs.map((tab) => ({
          label: tab.label,
          onClick: () => onTabChange(tab.value),
          isActive: activeTab === tab.value,
        }))}
      />
    </div>
  );
}

export default ProgramTabs;
